import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoTrash, IoReorderThree } from "react-icons/io5";
import { ReactSortable as Sortable } from "react-sortablejs";
import { toast } from "react-hot-toast";
import api from "../../../../../services/api";
import { AddWorkshopModal } from "../../../components/AddWorkshopModal";
import { ReorderListModal } from "../../../components/ReorderListModal";
import { NewChapterModal } from "../../../components/NewChapterModal";
import FileInput from "../../../../../components/FileInput";

export default function Edit() {
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [workshops, setWorkshops] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { id } = useParams();

  useEffect(() => {
    getTraining();
    getWorkshops();
  }, [id]);

  const getTraining = async () => {
    const { data, ok } = await api.get(`/training/${id}`);
    if (!ok) throw new Error("Failed to fetch training");
    setTraining(data);
  };

  const getWorkshops = async () => {
    try {
      setIsLoading(true);
      const { data, ok } = await api.post("/training_workshop/search", { training_id: id });
      if (!ok) throw new Error("Failed to fetch workshops");
      setWorkshops(data);
    } catch (error) {
      toast.error("Failed to fetch workshops");
    } finally {
      setIsLoading(false);
    }
  };

  function getSortedWorkshopsByChapter() {
    if (!training?.chapter_order) return [];

    const chapterMap = new Map();
    training.chapter_order.forEach((chapter) => chapterMap.set(chapter, []));

    workshops.forEach((workshop) => {
      if (chapterMap.has(workshop.chapter)) {
        chapterMap.get(workshop.chapter).push(workshop);
      }
    });

    chapterMap.forEach((workshops) => {
      workshops.sort((a, b) => a.position - b.position);
    });

    return training.chapter_order.map((chapter) => [chapter, chapterMap.get(chapter)]);
  }

  const handleUpdateField = async (field, value) => {
    try {
      if (field === "title" && !value.trim()) return toast.error("Title cannot be empty");

      const updatedTraining = { ...training, [field]: value };
      setTraining(updatedTraining);

      const { ok } = await api.put(`/training/${training._id}`, { [field]: value });

      if (!ok) throw new Error(`Failed to update training ${field}`);

      toast.success(`Training ${field} updated successfully!`);
    } catch (error) {
      setTraining(training);
      toast.error(`Failed to update training ${field}`);
    }
  };

  const handleUpdateChapter = async (oldChapter, newChapter) => {
    try {
      if (!newChapter.trim()) return toast.error("Chapter cannot be empty");

      if (newChapter === oldChapter) return;

      const isNewChapter = !training.chapter_order.includes(oldChapter);

      if (isNewChapter) {
        const updatedChapterOrder = [...training.chapter_order, newChapter];

        const { ok } = await api.put(`/training/${training._id}`, {
          chapter_order: updatedChapterOrder,
        });

        if (!ok) throw new Error("Failed to create new chapter");

        setTraining({ ...training, chapter_order: updatedChapterOrder });
        toast.success("New chapter created successfully!");
        return;
      }

      const workshopsToUpdate = workshops.filter((w) => w.chapter === oldChapter);
      const workshopIds = workshopsToUpdate.map((w) => w._id);

      const updatedChapterOrder = training.chapter_order.map((chapter) => (chapter === oldChapter ? newChapter : chapter));

      setTraining({ ...training, chapter_order: updatedChapterOrder });
      const updatedWorkshops = workshops.map((w) => (w.chapter === oldChapter ? { ...w, chapter: newChapter } : w));
      setWorkshops(updatedWorkshops);

      const trainingUpdateResponse = await api.put(`/training/${training._id}`, { chapter_order: updatedChapterOrder });

      if (!trainingUpdateResponse.ok) throw new Error("Failed to update training chapter order");

      const { ok } = await api.put("/training_workshop/many", { training_id: id, old_chapter: oldChapter, new_chapter: newChapter, workshop_ids: workshopIds });

      if (!ok) throw new Error("Failed to update workshop chapters");

      toast.success("Workshop chapters updated successfully!");
    } catch (error) {
      setTraining(training);
      setWorkshops(workshops);
      toast.error("Failed to update workshop chapters");
    }
  };

  const handleDeleteChapter = async (chapter) => {
    try {
      if (!window.confirm(`Are you sure you want to delete the chapter "${chapter}" and all its workshops?`)) return;

      const updatedWorkshops = workshops.filter((w) => w.chapter !== chapter);
      const updatedChapterOrder = training.chapter_order.filter((t) => t !== chapter);
      setWorkshops(updatedWorkshops);
      setTraining({ ...training, chapter_order: updatedChapterOrder });

      const { ok } = await api.remove(`/training_workshop/many?training_id=${training._id}&chapter=${encodeURIComponent(chapter)}`);
      if (!ok) throw new Error("Failed to delete chapter");

      await api.put(`/training/${training._id}`, { chapter_order: updatedChapterOrder });

      toast.success("Chapter and its workshops removed successfully!");
    } catch (error) {
      setTraining(training);
      setWorkshops(workshops);
      toast.error("Failed to remove chapter and its workshops");
    }
  };

  const handleReorderWorkshops = async (chapter, newOrder) => {
    try {
      const items = newOrder.map((workshop, index) => ({
        _id: workshop._id,
        position: index,
        chapter: chapter,
      }));

      const response = await api.put(`/training_workshop/positions/update`, { items });
      if (response.error) throw new Error(response.error);

      const updatedWorkshops = workshops.map((workshop) => {
        const newPosition = newOrder.findIndex((w) => w._id === workshop._id);
        return newPosition !== -1 ? { ...workshop, position: newPosition } : workshop;
      });
      setWorkshops(updatedWorkshops);

      toast.success("Workshop order updated successfully!");
    } catch (error) {
      toast.error(`Failed to update workshop order: ${error.message}`);
    }
  };

  const handleDeleteWorkshop = async (workshop) => {
    try {
      const response = await api.remove(`/training_workshop/${workshop._id}`);
      if (response.error) throw new Error(response.error);

      setWorkshops(workshops.filter((w) => w._id !== workshop._id));

      toast.success("Workshop removed successfully!");
    } catch (error) {
      toast.error("Failed to remove workshop");
    }
  };

  const handleReorderChapters = async (newOrder) => {
    try {
      const newChapterOrder = newOrder.map((item) => item.toString());

      const { ok } = await api.put(`/training/${training._id}`, { chapter_order: newChapterOrder });

      if (!ok) throw new Error("Failed to update chapter order");

      setTraining({ ...training, chapter_order: newChapterOrder });

      toast.success("Chapter order updated successfully!");
    } catch (error) {
      toast.error("Failed to update chapter order");
    }
  };

  const handleDeleteTraining = async () => {
    try {
      if (!window.confirm("Are you sure you want to delete this training?")) return;

      const { ok: workshopsDeleted } = await api.remove(`/training_workshop/many?training_id=${training._id}`);
      if (!workshopsDeleted) throw new Error("Failed to delete training workshops");

      const { ok } = await api.remove(`/training/${training._id}`);
      if (!ok) throw new Error("Failed to delete training");

      toast.success("Training and its workshops deleted successfully!");
      navigate("/training");
    } catch (error) {
      toast.error("Failed to delete training");
    }
  };

  const handleWorkshopAdded = (updatedTraining, newWorkshop) => {
    setTraining(updatedTraining);
    setWorkshops([...workshops, newWorkshop]);
  };

  const handleNewChapter = async (chapter) => {
    try {
      const updatedChapterOrder = [chapter, ...training.chapter_order];
      const { ok } = await api.put(`/training/${training._id}`, { chapter_order: updatedChapterOrder });

      if (!ok) throw new Error("Failed to create new chapter");

      setTraining({ ...training, chapter_order: updatedChapterOrder });
      getTraining();
      getWorkshops();
      toast.success("New chapter created successfully!");
    } catch (error) {
      toast.error("Failed to create new chapter");
    }
  };

  const renderHeader = () => (
    <div className="relative w-full flex flex-col">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={training.title || ""}
            onChange={(e) => handleUpdateField("title", e.target.value)}
            onBlur={(e) => {
              if (e.target.value !== training.title) {
                handleUpdateField("title", e.target.value);
              }
            }}
            className="text-2xl font-bold text-gray-900 px-2 py-1 focus:outline-none bg-transparent w-full hover:bg-gray-50 rounded-md"
          />
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 w-full">
        <textarea
          value={training.description || ""}
          onChange={(e) => {
            const updatedTraining = { ...training, description: e.target.value };
            setTraining(updatedTraining);
          }}
          onBlur={(e) => {
            if (e.target.value !== training.description) {
              handleUpdateField("description", e.target.value);
            }
          }}
          className="w-full text-gray-600 p-2 focus:outline-none hover:bg-gray-50 rounded-md min-h-[100px] resize-y"
        />
      </div>

      <div className="mt-4 flex items-start gap-2 w-full">
        <select
          value={training.category}
          onChange={(e) => handleUpdateField("category", e.target.value)}
          className="w-full text-gray-600 p-2 focus:outline-none hover:bg-gray-50 rounded-md">
          <option value="tech">Tech</option>
          <option value="product">Product</option>
          <option value="business">Business</option>
        </select>
      </div>

      <div className="mt-4 flex items-start gap-2 w-full">
        <FileInput
          value={training.image || ""}
          folder="/training"
          onChange={(e) => {
            handleUpdateField("image", e.target.value[0]);
          }}
        />
      </div>
    </div>
  );

  if (!training) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start gap-8">
          <div className="flex-1">{renderHeader()}</div>
          <div className="flex-shrink-0 flex flex-col gap-2">
            <ReorderListModal items={training.chapter_order} onReorder={handleReorderChapters}>
              <button className="flex items-center justify-center gap-1.5 px-3 py-[7px] text-sm font-medium text-gray-700 hover:text-gray-900 bg-white border border-gray-300 rounded-md hover:bg-gray-50 w-[150px]">
                <IoReorderThree className="w-3.5 h-3.5" />
                <span>Reorder Chapters</span>
              </button>
            </ReorderListModal>
            <button
              onClick={handleDeleteTraining}
              className="flex items-center justify-center gap-1.5 px-3 py-[7px] text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md w-[150px]">
              <IoTrash className="w-3.5 h-3.5" />
              <span>Delete Training</span>
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {!isLoading && (
            <div className="space-y-3">
              <div className="bg-white/60 rounded border border-gray-200 border-dashed">
                <div className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50/60 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-medium text-gray-400">New Chapter</h3>
                  </div>
                </div>
                <NewChapterModal onDone={handleNewChapter} />
              </div>

              {getSortedWorkshopsByChapter().map(([chapter, chapterWorkshops]) => (
                <div key={chapter} className="bg-white rounded border border-gray-200">
                  <div className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        defaultValue={chapter}
                        onBlur={(e) => {
                          if (e.target.value !== chapter) {
                            handleUpdateChapter(chapter, e.target.value);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.target.blur();
                          }
                        }}
                        className="px-1.5 py-0.5 text-base font-medium bg-transparent hover:bg-white focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDeleteChapter(chapter)} className="flex items-center justify-center px-2 py-1.5 text-white bg-red-600 hover:bg-red-700 rounded">
                        <IoTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {/* Workshop list for this chapter */}
                  <Sortable
                    list={chapterWorkshops}
                    setList={(newOrder) => {
                      const hasOrderChanged = chapterWorkshops.some((workshop, index) => workshop._id !== newOrder[index]._id);
                      if (hasOrderChanged) {
                        handleReorderWorkshops(chapter, newOrder);
                      }
                    }}
                    animation={150}
                    handle=".workshop-drag-handle"
                    className="divide-y divide-gray-200"
                    ghostClass="opacity-50">
                    {chapterWorkshops.map((workshop) => (
                      <div key={workshop._id} className="flex items-center justify-between px-2.5 py-1.5">
                        <div className="flex items-center gap-2">
                          <IoReorderThree className="w-4 h-4 text-gray-400 cursor-move workshop-drag-handle" />
                          {(workshop.workshop_image || workshop.image) === "/default-workshop.png" ? (
                            <div className="w-5 h-5 rounded flex-shrink-0 border border-gray-200 border-dashed" />
                          ) : (
                            <div className="w-5 h-5 rounded flex-shrink-0">
                              <img
                                src={workshop.workshop_image || workshop.image || "/default-workshop.png"}
                                alt={workshop.workshop_name || workshop.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/default-workshop.png";
                                }}
                              />
                            </div>
                          )}
                          <span className="text-gray-900 text-sm">{workshop.workshop_name || workshop.name}</span>
                        </div>
                        <button onClick={() => handleDeleteWorkshop(workshop)} className="text-red-600 hover:text-red-700">
                          <IoTrash className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </Sortable>
                  <AddWorkshopModal training={training} defaultChapter={chapter} onDone={handleWorkshopAdded} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
